import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './Stats.scss';
import { Column } from 'primereact/column';
import { DataTable, DataTableExpandedRows, DataTableRowToggleEvent } from 'primereact/datatable';
import {
  MonitorDataRecord,
  MonitorDataResponse,
} from '../../../../../shared/models/monitor/MonitorDataResponse';
import { ChartMatrixData, GeneralData, LinksData, StatsTableData } from './models/StatsModel';
import { Dropdown } from 'primereact/dropdown';
import { useFilters } from '../../../../../shared/contexts/filters/FiltersContext';
import { useTranslation } from 'react-i18next';
import {
  createBrandsMatrix,
  createKeywordMatrix,
  filterSourcesByDomain,
  findSelectedDomain,
  getDomainsOptions,
  getKeywordMap,
  getMonitorTypeIcon,
  prepareAndGetColorsForPieChart,
  prepareGeneralData,
  prepareStatsTableData,
} from './helpers/StatsHelper';
import {
  expandSelectedChatTypes,
  getMonitorName,
  getMonitorTypesForChatType,
  sortMonitorTypesByDisplayOrder,
} from '../../../../../shared/helpers/MonitorTypeHelpers';
import { buildBucketLabelFormatters } from '../../../../../shared/helpers/date/bucketDateLabels';
import { endOfDay, isSameDay } from 'date-fns';
import EmptyState from '../../../../../shared/components/common/empty-state/EmptyState';
import { EmptyStateData } from '../../../../../shared/components/common/empty-state/models/EmptyStateData';
import LineChart from '../../../../../shared/components/chart/line-chart/LineChart';
import { normalizeKeyword, SELECTED_BRAND_COLOR } from '../../../../../shared/utils/utils';
import { getSourceHref } from '../../../../../shared/utils/urls';
import { SOV_PIE_CHART_PALETTE } from '../../../../../shared/utils/sovPalette';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import { createCustomColumn, modelTemplate } from '../../../../../shared/helpers/ColumnHelper';
import PieChartWithLegend from '../../../../../shared/components/chart/pie-chart-with-legend/PieChartWithLegend';
import { Skeleton } from 'primereact/skeleton';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import {
  calculatePercentageShare,
  getAllSourcesCount,
  getMonitorTypeByChatOptionsType,
} from '../../../../../shared/helpers/StatsHelpers';
import { ChatType } from '../../../../../shared/types/monitor/ChatType';
import { DropdownItemData } from '../../../../../shared/components/dropdown-item/DropdownItem';
import { PromptResponse } from '../../../../../shared/models/monitor/PromptResponse';
import { KeywordsDailyStatsResponse } from '../../../../../shared/models/monitor/keywords/stats/KeywordsDailyStatsResponse';
import { MonitorEventLogResponse } from '../../../../../shared/models/monitor/MonitorEventLogResponse';
import { MonitorEventType } from '../../../../../shared/models/monitor/MonitorEventType';
import { KeywordResponse } from '../../../../../shared/models/monitor/KeywordResponse';
import { SourceDataResponse } from '../../../../../shared/models/monitor/data/sources/SourceDataResponse';
import { Button } from 'primereact/button';
import { TagsResponse } from '../../../../../shared/models/tags/TagsResponse';
import { findTagIdInKeyword } from '../../../../../shared/helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../../../shared/helpers/tags/TagsHelper';
import { PaginationData } from '../../../../../shared/models/monitor/data/sources/PaginationData';
import {
  calculatePaginationValues,
  calculateSafeFirst,
  createPaginationHandler,
} from '../../../../../shared/helpers/PaginationHelper';
import { Tooltip } from 'primereact/tooltip';
import ChatResponse from './components/chat-response/ChatResponse';
import { getMonitorData } from '../../../../../shared/api/services/monitor/data/MonitorDataService';
import { formatEventsForLineChart } from '../../../../../shared/services/monitor/events/EventNameFormatter';
import { getPanelSettings } from '../../../../../shared/services/user/UserService';

interface StatsProps {
  promptData: PromptResponse;
  monitorData: MonitorDataResponse | null;
  sourcesData: SourceDataResponse;
  keywordResponse: KeywordResponse[];
  keywordsDailyStatsResponse: KeywordsDailyStatsResponse | null;
  keywordsDailyStatsWithoutAggregateResponse: KeywordsDailyStatsResponse | null;
  monitorEventsData: MonitorEventLogResponse[] | null;
  exportSources: () => void;
  exportAiAnswers: (keyword: string) => Promise<void>;
  onPageChange: (page: number, pageSize: number) => void;
  pagination?: PaginationData;
  canExport: boolean | null;
  canExportLlmResponse: boolean | null;
  tags: TagsResponse[];
  isLoading: boolean;
}

const Stats: React.FC<StatsProps> = ({
  promptData,
  monitorData,
  sourcesData,
  keywordResponse,
  keywordsDailyStatsResponse,
  keywordsDailyStatsWithoutAggregateResponse,
  monitorEventsData,
  exportSources,
  exportAiAnswers,
  onPageChange,
  pagination,
  canExport,
  canExportLlmResponse,
  tags,
  isLoading,
}) => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { monitorPeriod, selectedFirm, range, domain, setDomain, selectedChatType, selectedChatTypes } =
    useFilters();

  // Locale + period aware date labels for the chart X-axis (compact range) and
  // tooltip (full range with year). The matrix keeps its raw ISO keys/ranges
  // (prepareHeaderDate output) so getMarksData's parseISO still positions events.
  const dateLabelFormatters = useMemo(
    () => buildBucketLabelFormatters(monitorPeriod, i18n.language),
    [monitorPeriod, i18n.language]
  );

  const [, setMainChartLegendMap] = useState<Map<string, boolean> | null>(null);
  const [, setBrandsChartLegendMap] = useState<Map<string, boolean> | null>(null);
  const [monitorHeaders, setMonitorHeaders] = useState<string[]>([]);
  const [brandHeadersMap, setBrandHeadersMap] = useState<string[]>([]);
  const [keywordMatrixData, setKeywordMatrixData] = useState<ChartMatrixData | null>(null);
  const [brandsMatrixData, setBrandsMatrixData] = useState<ChartMatrixData | null>(null);
  const [generalData, setGeneralData] = useState<GeneralData | null>(null);
  const [sources, setSources] = useState<LinksData[]>([]);
  const [domains, setDomains] = useState<string[]>([]);
  const [dailyMonitorResponse, setDailyMonitorResponse] = useState<MonitorDataResponse | null>(null);
  const [tableData, setTableData] = useState<StatsTableData[]>([]);
  const [expandedRows, setExpandedRows] = useState<any>(null);
  const [isGeneratingStats, setGeneratingStats] = useState<boolean>(true);
  const [brandColors, setBrandColors] = useState<{ name: string; color: string }[] | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingAiAnswers, setIsDownloadingAiAnswers] = useState(false);
  const [isDataProcessed, setIsDataProcessed] = useState<boolean>(false);
  const [expandedRowsData, setExpandedRowsData] = useState<Record<string, MonitorDataRecord[]>>({});
  const [loadingRows, setLoadingRows] = useState<Set<string>>(new Set());
  const [errorRows, setErrorRows] = useState<Set<string>>(new Set());
  const [chartMarkerSettings, setChartMarkerSettings] = useState<Record<string, boolean>>({});
  const expandedRowsDataRef = useRef<Record<string, MonitorDataRecord[]>>({});
  const loadingRowsRef = useRef<Set<string>>(new Set());
  const chatDateScrollRef = useRef<boolean>(false);
  const pendingScrollChatId = useRef<string | null>(null);
  const tableRef = useRef<DataTable<StatsTableData[]>>(null);

  const hasExplicitModelFilter = searchParams.get('models') !== null;

  const allowedMonitorTypes = useMemo(() => {
    if (!hasExplicitModelFilter) return null;
    if (!selectedChatTypes || selectedChatTypes.length === 0) return new Set<string>();
    const expanded = expandSelectedChatTypes(selectedChatTypes);
    const types = new Set<string>();
    for (const ct of Array.from(expanded)) {
      for (const mt of getMonitorTypesForChatType(ct as ChatType)) {
        types.add(mt);
      }
    }
    return types;
  }, [hasExplicitModelFilter, selectedChatTypes]);

  const handlePageChange = createPaginationHandler(pagination, onPageChange);

  const { first, rows, totalRecords } = calculatePaginationValues(pagination, sources.length);

  const safeFirst = calculateSafeFirst(first, rows, totalRecords);

  const noDataBrandPositioningEmptyState = {
    title: t('noPositioningData'),
    content: t('noPositioningDataDesc'),
  } as EmptyStateData;

  useEffect(() => {
    if (monitorData === null) {
      return;
    }

    prepareData(monitorData);
    setIsDataProcessed(true);
    setExpandedRows(null);
    setExpandedRowsData({});
    setLoadingRows(new Set());
    setErrorRows(new Set());
    expandedRowsDataRef.current = {};
    loadingRowsRef.current = new Set();
  }, [monitorData]);

  useEffect(() => {
    if (monitorData === null || !allowedMonitorTypes) {
      return;
    }
    prepareData(monitorData);
  }, [allowedMonitorTypes]);

  useEffect(() => {
    if (!sourcesData) {
      return;
    }

    setSources(sourcesData.linksData);
    setDomains(sourcesData.domains);
  }, [sourcesData]);

  useEffect(() => {
    let isMounted = true;

    const loadPanelSettings = async (): Promise<void> => {
      try {
        const panelSettings = await getPanelSettings();
        const chartSettings = panelSettings?.charts ?? {};

        if (!isMounted) {
          return;
        }

        setChartMarkerSettings({
          brandPositioningOverview:
            typeof chartSettings.brandPositioningOverview?.displayMarkers === 'boolean'
              ? chartSettings.brandPositioningOverview.displayMarkers
              : true,
          vsTopCompetitors:
            typeof chartSettings.vsTopCompetitors?.displayMarkers === 'boolean'
              ? chartSettings.vsTopCompetitors.displayMarkers
              : true,
        });
      } catch (_error) {
        if (isMounted) {
          setChartMarkerSettings({
            brandPositioningOverview: true,
            vsTopCompetitors: true,
          });
        }
      }
    };

    void loadPanelSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  const now = new Date();
  now.setMilliseconds(0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleExportSources = async () => {
    if (isDownloading || !canExport) {
      return;
    }

    setIsDownloading(true);

    try {
      exportSources();
    } finally {
      setIsDownloading(false);
    }
  };

  const handleExportAiAnswers = async () => {
    if (isDownloadingAiAnswers || !canExportLlmResponse) {
      return;
    }

    setIsDownloadingAiAnswers(true);
    try {
      if (!promptData.keyword) {
        return;
      }
      await exportAiAnswers(promptData.keyword);
    } finally {
      setIsDownloadingAiAnswers(false);
    }
  };

  const isInRange = useMemo(() => {
    if (!range || !range[0] || !range[1]) {
      return false;
    }

    const now = new Date();
    const today = new Date();
    const createdInDate = new Date(promptData.created);

    const rangeStart = new Date(range[0]);
    const rangeEnd = new Date(range[1]);

    const finalRangeEnd = isSameDay(rangeEnd, today) ? now : endOfDay(rangeEnd);
    const bothBefore = rangeStart < createdInDate && finalRangeEnd < createdInDate;
    return !bothBefore;
  }, [range, promptData]);

  const allowedEventTypes: MonitorEventType[] = ['MODEL_ADDED', 'MODEL_DELETED', 'MODEL_RELEASED'];

  const filteredMonitorEvents = useMemo(
    () => (monitorEventsData ?? []).filter((event) => allowedEventTypes.includes(event.type)),
    [monitorEventsData]
  );

  const monitorChartEvents = useMemo(
    () => formatEventsForLineChart(filteredMonitorEvents),
    [filteredMonitorEvents, i18n.language]
  );

  const isMonitorDataEmpty = useMemo(() => {
    if (!isDataProcessed || !range || !range[0] || !range[1]) {
      return false;
    }

    const rangeStart = new Date(range[0]);
    const rangeEnd = new Date(range[1]);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDay = new Date(rangeStart);
    startDay.setHours(0, 0, 0, 0);

    const endDay = new Date(rangeEnd);
    endDay.setHours(0, 0, 0, 0);

    const isTodayRange = startDay.getTime() === today.getTime() && endDay.getTime() === today.getTime();
    return tableData && tableData.length === 0 && !isTodayRange;
  }, [tableData, isDataProcessed, range]);

  useEffect(() => {
    if (monitorData === null || !isDataProcessed || generalData === null) {
      return;
    }

    const shouldGenerateStats = isMonitorDataEmpty && isInRange;
    setGeneratingStats(shouldGenerateStats);
  }, [isMonitorDataEmpty, isInRange, monitorData, isDataProcessed]);

  const prepareData = (monitorData: MonitorDataResponse): void => {
    if (!monitorPeriod || !monitorData) {
      return;
    }

    setKeywordMatrixData(null);
    setBrandsMatrixData(null);

    const keywordsDailyStatsData =
      keywordsDailyStatsResponse && keywordsDailyStatsResponse.keywordsDailyStatsData
        ? keywordsDailyStatsResponse.keywordsDailyStatsData
        : null;
    const keywordsDailyStatsRecord = keywordsDailyStatsData
      ? keywordsDailyStatsData[promptData.keyword]
      : [];

    const keywordsDailyStatsWithoutAggregateData =
      keywordsDailyStatsWithoutAggregateResponse &&
      keywordsDailyStatsWithoutAggregateResponse.keywordsDailyStatsData
        ? keywordsDailyStatsWithoutAggregateResponse.keywordsDailyStatsData
        : null;

    const keywordsDailyStatsWithoutAggregateRecord = keywordsDailyStatsWithoutAggregateData
      ? keywordsDailyStatsWithoutAggregateData[promptData.keyword]
      : [];

    const dataForTable: MonitorDataResponse = allowedMonitorTypes
      ? {
          data: Object.fromEntries(
            Object.entries(monitorData.data)
              .map(([date, records]) => [date, records.filter((r) => allowedMonitorTypes.has(r.type))])
              .filter(([, records]) => (records as MonitorDataRecord[]).length > 0)
          ),
          pagination: monitorData.pagination,
        }
      : monitorData;

    const statsTable = prepareStatsTableData(
      promptData.prompt,
      monitorPeriod,
      dataForTable,
      keywordsDailyStatsWithoutAggregateRecord
    );

    if (statsTable) {
      if (allowedMonitorTypes) {
        statsTable.statsTableData = statsTable.statsTableData.map((row) => {
          const records = dataForTable.data[row.created] || [];
          const positions = records.map((r) => r.position).filter((p) => p > 0);
          const avgPos =
            positions.length > 0
              ? (positions.reduce((a, b) => a + b, 0) / positions.length).toFixed(1)
              : '-';
          const sovValues = records.map((r) => r.shareOfVoice).filter((s) => s != null && s > 0);
          const avgSov =
            sovValues.length > 0
              ? (sovValues.reduce((a, b) => a + b, 0) / sovValues.length).toFixed(1) + '%'
              : '-';
          return { ...row, averagePosition: avgPos, averageShareOfVoice: avgSov };
        });
      }
      setTableData(statsTable.statsTableData);
      setDailyMonitorResponse(statsTable.dailyMonitorResponse);
    }

    if (keywordsDailyStatsData && Object.keys(keywordsDailyStatsData).length > 0) {
      const keywordsMap = getKeywordMap(keywordResponse);
      const keywordMatrix = createKeywordMatrix(monitorPeriod, keywordsDailyStatsRecord);
      const brandsMatrix = createBrandsMatrix(
        monitorPeriod,
        keywordsDailyStatsData,
        promptData.keyword,
        keywordsMap,
        keywordResponse,
        tags
      );
      const generalDataStructure = prepareGeneralData(
        keywordsDailyStatsData,
        promptData.keyword,
        keywordsMap,
        keywordResponse,
        tags
      );

      setKeywordMatrixData(keywordMatrix);
      setBrandsMatrixData(brandsMatrix);

      if (generalDataStructure && selectedFirm) {
        const colors = prepareAndGetColorsForPieChart(
          generalDataStructure.shareOfVoiceChartData,
          firmName()
        );
        setBrandColors(colors);
      }

      setMonitorHeaders([t('averagePosition')]);
      setBrandHeadersMap(prepareAndGetBrandsHeaderMap(brandsMatrix.matrix));
      setGeneralData(generalDataStructure);
    } else {
      setGeneralData({
        shareOfVoiceChartData: [],
        cumulativeData: {},
      });
    }
  };

  const prepareAndGetBrandsHeaderMap = (brandsMatrix: (string | number)[][]) => {
    const headers = [];
    for (let i = 1; i < brandsMatrix[0].length; i++) {
      headers.push(brandsMatrix[0][i].toString());
    }

    return headers;
  };

  const onLegendMainChartChange = useCallback(
    (legendStateMap: Map<string, boolean>) => setMainChartLegendMap(legendStateMap),
    [monitorData]
  );

  const onLegendBrandsChartChange = useCallback(
    (legendStateMap: Map<string, boolean>) => setBrandsChartLegendMap(legendStateMap),
    [monitorData]
  );

  const modelFilteredSources = useMemo(() => {
    if (!allowedMonitorTypes) return sources;
    return sources.filter((s) => s.models.some((m) => allowedMonitorTypes.has(m)));
  }, [sources, allowedMonitorTypes]);

  const filteredSources = useMemo(
    () => filterSourcesByDomain(domain, modelFilteredSources),
    [modelFilteredSources, domain]
  );

  const domainsOptions: DropdownItemData<string>[] = useMemo(
    () => getDomainsOptions(domains, domain),
    [domains, domain, t]
  );

  const selectedOption = useMemo(
    () => findSelectedDomain(domain, domainsOptions),
    [domain, domainsOptions]
  );

  const handleDomainChange = (name: string) => {
    if (name === t('allDomains')) {
      setDomain(null);
    } else {
      setDomain(name);
    }
  };

  const getTrends = (trends: number | undefined) => {
    if (trends === undefined) {
      return t('noData');
    } else if (trends === 0) {
      return (
        <div className="trends trends--grey">
          <label className="trends__icon">－</label>
        </div>
      );
    } else if (trends < 0) {
      return (
        <div className="trends trends--red">
          <label className="trends__icon">south</label>
          <label>{Math.abs(trends)}</label>
        </div>
      );
    } else if (trends > 0) {
      return (
        <div className="trends trends--green">
          <label className="trends__icon">north</label>
          <label>{Math.abs(trends)}</label>
        </div>
      );
    }
  };

  const firmName = () => {
    if (!selectedFirm) {
      return '';
    }

    if (!tags || !keywordResponse) {
      return selectedFirm.name;
    }
    const normalized = normalizeKeyword(selectedFirm.name).toLowerCase();
    const tagId = findTagIdInKeyword(normalized, keywordResponse);
    const keyword = getTagLabel(tags, tagId, normalized);
    const keywordsMap = getKeywordMap(keywordResponse);

    return keywordsMap.get(keyword) ?? keyword;
  };

  const loadRowContent = useCallback(
    async (dateKey: string) => {
      if (expandedRowsDataRef.current[dateKey] || loadingRowsRef.current.has(dateKey)) {
        return;
      }

      loadingRowsRef.current.add(dateKey);
      setLoadingRows((prev) => new Set(prev).add(dateKey));

      try {
        const chatType = selectedChatType ? getMonitorTypeByChatOptionsType(selectedChatType) : 'ALL';

        const response = await getMonitorData(
          promptData.id,
          promptData.keyword,
          monitorPeriod ?? 'DAILY',
          chatType,
          dateKey,
          dateKey,
          1,
          100,
          true
        );

        const records = response.data[dateKey] || [];

        expandedRowsDataRef.current = { ...expandedRowsDataRef.current, [dateKey]: records };
        setExpandedRowsData((prev) => ({ ...prev, [dateKey]: records }));
        setErrorRows((prev) => {
          const next = new Set(prev);
          next.delete(dateKey);
          return next;
        });
      } catch (error) {
        console.error('Failed to load row content:', error);
        setErrorRows((prev) => new Set(prev).add(dateKey));
      } finally {
        loadingRowsRef.current.delete(dateKey);
        setLoadingRows((prev) => {
          const next = new Set(prev);
          next.delete(dateKey);
          return next;
        });
      }
    },
    [promptData, selectedChatType, monitorPeriod]
  );

  const handleRowToggle = useCallback(
    (e: DataTableRowToggleEvent) => {
      setExpandedRows(e.data);

      if (e.data && !Array.isArray(e.data)) {
        const expandedRows = e.data as DataTableExpandedRows;
        Object.keys(expandedRows).forEach((dateKey) => {
          if (expandedRows[dateKey] && !expandedRowsDataRef.current[dateKey]) {
            loadRowContent(dateKey);
          }
        });
      }
    },
    [loadRowContent]
  );

  useEffect(() => {
    const chatDate = searchParams.get('chatDate');
    const chatId = searchParams.get('chatId');
    if (!chatDate || chatDateScrollRef.current || tableData.length === 0) return;

    const dateExists = tableData.some((row) => row.created === chatDate);
    if (!dateExists) return;

    chatDateScrollRef.current = true;
    pendingScrollChatId.current = chatId;
    setExpandedRows({ [chatDate]: true });
    loadRowContent(chatDate);

    searchParams.delete('chatDate');
    searchParams.delete('chatId');
    setSearchParams(searchParams, { replace: true });
  }, [tableData, searchParams, loadRowContent, setSearchParams]);

  useEffect(() => {
    const chatId = pendingScrollChatId.current;
    if (!chatId) return;

    const target = document.getElementById(`chat-${chatId}`);
    if (!target) return;

    pendingScrollChatId.current = null;
    requestAnimationFrame(() => {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [expandedRowsData]);

  const rowExpansionTemplate = (statsTableData: StatsTableData) => {
    const dateKey = statsTableData.created.toString();
    const records = expandedRowsData[dateKey];
    const isLoading = loadingRows.has(dateKey) || (!records && !errorRows.has(dateKey));

    if (isLoading) {
      return (
        <div className="table-message table-message--loading">
          <span className="ico ico--loader" />
        </div>
      );
    }

    if (errorRows.has(dateKey)) {
      return (
        <div className="table-message table-message--loading">
          <span>{t('errorLoadingDataRetry')}</span>
        </div>
      );
    }

    const filteredRecords = allowedMonitorTypes
      ? records.filter((r) => allowedMonitorTypes.has(r.type))
      : records;

    if (filteredRecords.length === 0) {
      return <EmptyPanel />;
    }

    const sortedRecords = [...filteredRecords].sort(
      (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
    );

    return (
      <div className="table-message">
        {sortedRecords.map((monitorDataRecord, index) => (
          <div key={`chat-${dateKey}-${index}`} id={`chat-${monitorDataRecord.id}`}>
            <ChatResponse
              prompt={promptData.prompt}
              currentPosition={monitorDataRecord.position}
              created={monitorDataRecord.created}
              content={monitorDataRecord.content || ''}
              organicContent={monitorDataRecord.organicResponse || ''}
              htmlContent={monitorDataRecord.htmlResponse || ''}
              monitorType={monitorDataRecord.type}
              keyword={promptData.keyword}
              positions={monitorDataRecord.positionJson || []}
            />
          </div>
        ))}
      </div>
    );
  };

  const indexTemplate = (rowData: LinksData, { rowIndex }: { rowIndex: number }) => {
    return `#${rowIndex + 1}`;
  };

  const urlTemplate = (rowData: LinksData) => {
    const url = rowData.url;
    return (
      <div className="source-data">
        <span
          className="source-data__icon"
          style={{ backgroundImage: `url("https://favicon.is/${rowData.url}")` }}
        />
        <a href={getSourceHref(url)} target="_blank">
          {url}
        </a>
      </div>
    );
  };

  const allSourcesCount = getAllSourcesCount(modelFilteredSources);
  const shareOfVoiceForKeyword = generalData?.cumulativeData[promptData.keyword]?.averageShareOfVoice;

  return (
    <>
      <div className={`tiles tiles--stats tiles--page`}>
        <div className="tile-box">
          {!isGeneratingStats ? (
            <div className="tile-box__box">
              <label className="tile-box__name">
                {t('averagePosition')}
                <CustomTooltip tooltipText="tooltipAveragePosition" />
              </label>
              <label className="tile-box__value">
                {keywordsDailyStatsResponse?.averagePosition ||
                  generalData?.cumulativeData[promptData.keyword]?.averagePosition ||
                  t('noData')}
              </label>
            </div>
          ) : (
            <Skeleton className="skeleton--box">
              <span />
            </Skeleton>
          )}
        </div>
        <div className="tile-box">
          {!isGeneratingStats ? (
            <div className="tile-box__box">
              <label className="tile-box__name">
                {t('averageShareOfVoice')}
                <CustomTooltip tooltipText="tooltipShareOfVoice" />
              </label>
              <label className="tile-box__value">
                {shareOfVoiceForKeyword && shareOfVoiceForKeyword > 0
                  ? shareOfVoiceForKeyword + '%'
                  : t('noData')}
              </label>
            </div>
          ) : (
            <Skeleton className="skeleton--box">
              <span />
            </Skeleton>
          )}
        </div>
        <div className="tile-box">
          {!isGeneratingStats ? (
            <div className="tile-box__box">
              <label className="tile-box__name">
                {t('trends')}
                <CustomTooltip tooltipText="tooltipTrends" />
              </label>
              <label className="tile-box__value">
                {getTrends(generalData?.cumulativeData[promptData.keyword]?.trend) ?? undefined}
              </label>
            </div>
          ) : (
            <Skeleton className="skeleton--box">
              <span />
            </Skeleton>
          )}
        </div>
      </div>
      <div className="stats">
        <div className="stats__panel">
          <div className="page-box__title-action">
            <div className="page-box__title">{t('brandPositioningOverview')}</div>
          </div>
          <div className={`stats-box stats-box--chart ${isMonitorDataEmpty && 'stats-box--empty'}`}>
            {keywordMatrixData &&
            keywordMatrixData.matrix.length > 0 &&
            !isMonitorDataEmpty &&
            isInRange ? (
              <div className="stats-box__content">
                <LineChart
                  firstData={keywordMatrixData.matrix}
                  firstColorPalette={[SELECTED_BRAND_COLOR]}
                  headers={monitorHeaders}
                  onLegendClick={onLegendMainChartChange}
                  inverseY={true}
                  showOutside30={true}
                  marksData={monitorChartEvents}
                  panelSettingsConfig="brandPositioningOverview"
                  prefetchedDisplayMarkers={chartMarkerSettings.brandPositioningOverview}
                  keywordsMapper={getKeywordMap(keywordResponse)}
                  tags={tags}
                  commonKeywords={keywordResponse}
                  dateAxisFormatter={dateLabelFormatters.axis}
                  dateTooltipFormatter={dateLabelFormatters.tooltip}
                />
              </div>
            ) : isMonitorDataEmpty && monitorData !== null ? (
              <EmptyPanel />
            ) : (
              <div className="stats-box__content">
                <Skeleton className="skeleton--chart">
                  <span />
                </Skeleton>
              </div>
            )}
          </div>
        </div>

        <div className="stats__panel">
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {t('averageShareOfVoice')}
              <CustomTooltip tooltipText="tooltipShareOfVoice" />
            </div>
          </div>
          <div className={`stats-box stats-box--pie-chart ${isMonitorDataEmpty && 'stats-box--empty'}`}>
            <div className="stats-box__content">
              {generalData &&
              generalData.shareOfVoiceChartData.length > 0 &&
              !isMonitorDataEmpty &&
              isInRange ? (
                <>
                  <>
                    {/*<Button*/}
                    {/*  id={`btn-settings-pie`}*/}
                    {/*  className="action-btn"*/}
                    {/*  onClick={() =>*/}
                    {/*    confirmNewProjectKeywords(*/}
                    {/*      projectIdNumber,*/}
                    {/*      getProjectName(project[0]),*/}
                    {/*      allProjectKeywords*/}
                    {/*    )*/}
                    {/*  }*/}
                    {/*>*/}
                    {/*  <span className="ico--settings ico--xs" />*/}
                    {/*</Button>*/}
                    <CustomTooltip tooltipText="chooseBrandsToCompare" targetRef="btn-settings-pie" />
                  </>
                  <PieChartWithLegend
                    chartData={generalData.shareOfVoiceChartData}
                    colorPalette={SOV_PIE_CHART_PALETTE}
                    selectedBrand={firmName()}
                    size="SM"
                  />
                </>
              ) : isMonitorDataEmpty && monitorData !== null ? (
                <EmptyPanel />
              ) : (
                <Skeleton className="skeleton--chart-pie">
                  <span />
                  <span />
                  <span />
                </Skeleton>
              )}
            </div>
          </div>
        </div>
      </div>
      {brandsMatrixData && (
        <>
          <div className="page-box__title-action">
            <div className="page-box__title">{`${firmName()} ${t('vsTopCompetitors')}`}</div>
          </div>
          <div className="stats-box">
            <div className="stats-box__content">
              {brandsMatrixData.matrix.length > 0 && !isMonitorDataEmpty && isInRange ? (
                <LineChart
                  firstData={brandsMatrixData.matrix}
                  firstColorPalette={SOV_PIE_CHART_PALETTE}
                  headers={brandHeadersMap}
                  onLegendClick={onLegendBrandsChartChange}
                  inverseY={true}
                  selectedBrand={firmName()}
                  showOutside30={true}
                  legendType={'FLEX_START'}
                  colorsByName={brandColors || undefined}
                  marksData={monitorChartEvents}
                  panelSettingsConfig="vsTopCompetitors"
                  prefetchedDisplayMarkers={chartMarkerSettings.vsTopCompetitors}
                  keywordsMapper={getKeywordMap(keywordResponse)}
                  tags={tags}
                  commonKeywords={keywordResponse}
                  smooth={true}
                  dateAxisFormatter={dateLabelFormatters.axis}
                  dateTooltipFormatter={dateLabelFormatters.tooltip}
                />
              ) : isMonitorDataEmpty && monitorData !== null ? (
                <EmptyPanel />
              ) : (
                <EmptyState emptyStateData={noDataBrandPositioningEmptyState} mode="SMALL" />
              )}
            </div>
          </div>
        </>
      )}
      {modelFilteredSources?.length > 0 && (
        <>
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">
              {t('linkTracking')}
              <CustomTooltip tooltipText="linkTrackingTooltip" />
            </div>
            <div className="page-box__source-filter">
              <Dropdown
                value={selectedOption}
                placeholder={t('selectDomain')}
                onChange={(e) => handleDomainChange(e.value.name)}
                options={domainsOptions}
                className="dropdown"
                optionLabel="name"
                optionValue="type"
                filter
              />
              {!promptData.demoVersion && (
                <>
                  <Button
                    id="btn-settings-export-sources"
                    className={`action-btn  ${!canExport ? 'btn--disabled-tooltip' : ''}`}
                    onClick={() => handleExportSources()}
                  >
                    {isDownloading ? (
                      <span className="ico--loader ico--xs" />
                    ) : (
                      <span className={`ico--download ${!canExport ? 'ico--disabled' : ''} ico--xs`} />
                    )}
                  </Button>
                  <CustomTooltip
                    tooltipText={!canExport ? 'upgradePlanTrialExportMsg' : 'downloadSources'}
                    targetRef="btn-settings-export-sources"
                  />
                </>
              )}
            </div>
          </div>
          <DataTable
            stripedRows
            paginator={true}
            responsiveLayout="scroll"
            paginatorClassName="pagination"
            removableSort
            rows={10}
            rowsPerPageOptions={[10, 25, 50]}
            value={filteredSources}
            tableStyle={{ minWidth: '50rem', tableLayout: 'auto' }}
            tableClassName="table"
            className="table-wrapper"
            currentPageReportTemplate={t('rowsNumber') + ':'}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          >
            <Column
              field="index"
              header={t('position')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell table__cell--center"
              style={{ width: '50px' }}
              body={indexTemplate}
            />
            <Column
              field="url"
              header={t('url')}
              headerClassName="table__header"
              style={{ maxWidth: '400px' }}
              body={urlTemplate}
            />
            <Column
              field="model"
              header={t('model')}
              headerClassName="table__header"
              style={{ width: '200px' }}
              body={modelTemplate}
            />
            {createCustomColumn({
              field: 'count',
              body: (data: LinksData) => calculatePercentageShare(data.count, allSourcesCount),
              headerName: 'coverage',
              tooltip: t('coverageTooltip'),
              headerClassName: 'table__header table__header--center',
              bodyClassName: 'table__cell table__cell--center',
              style: { width: '50px' },
            })}
          </DataTable>
        </>
      )}

      {(!isGeneratingStats || monitorData !== null) && (
        <>
          <div className="page-box__title-action">
            <div className="page-box__title page-box__title--flex">{t('exploreAIAnswers')}</div>
            {!promptData.demoVersion && canExportLlmResponse && (
              <div className="page-box__source-filter">
                <Button
                  id="btn-export-ai-answers"
                  className={`action-btn  ${!canExportLlmResponse ? 'btn--disabled-tooltip' : ''}`}
                  onClick={handleExportAiAnswers}
                >
                  {isDownloadingAiAnswers ? (
                    <span className="ico--loader ico--xs" />
                  ) : (
                    <span
                      className={`ico--download ${!canExportLlmResponse ? 'ico--disabled' : ''} ico--xs`}
                    />
                  )}
                </Button>
                <CustomTooltip
                  tooltipText={!canExportLlmResponse ? 'upgradePlanTrialExportMsg' : 'downloadAiAnswers'}
                  targetRef="btn-export-ai-answers"
                />
              </div>
            )}
          </div>
          {isLoading ? (
            <Skeleton className="skeleton--table">
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </Skeleton>
          ) : (
            <DataTable
              ref={tableRef}
              stripedRows
              paginator
              lazy
              paginatorClassName="pagination"
              removableSort
              sortField="created"
              sortOrder={-1}
              rows={rows}
              first={safeFirst}
              onPage={handlePageChange}
              totalRecords={totalRecords}
              rowsPerPageOptions={[10, 25, 50]}
              value={tableData}
              tableStyle={{ minWidth: '50rem' }}
              tableClassName="table"
              className="table-wrapper table-wrapper--accordion"
              expandedRows={expandedRows}
              emptyMessage={() => <EmptyPanel />}
              onRowToggle={handleRowToggle}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="created"
              currentPageReportTemplate={t('rowsNumber') + ':'}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            >
              <Column
                sortable
                field="created"
                header={t('date')}
                headerClassName="table__header"
                style={{ width: '90px' }}
              />
              <Column
                field={undefined}
                header=""
                headerClassName="table__header"
                style={{ minWidth: '250px' }}
                body={(rowData: StatsTableData) => {
                  const records =
                    dailyMonitorResponse?.data[rowData.created] ??
                    monitorData?.data?.[rowData.created] ??
                    [];
                  const allTypes = Array.from(new Set(records.map((r) => r.type)));
                  const types = sortMonitorTypesByDisplayOrder(
                    allowedMonitorTypes ? allTypes.filter((t) => allowedMonitorTypes.has(t)) : allTypes
                  );
                  return (
                    <div className="table__cell-date-icons tags">
                      {types.map((type) => {
                        const iconClass = getMonitorTypeIcon(type);
                        if (!iconClass) return null;
                        const name = `tooltip-model-${type}-${rowData.created}`;
                        return (
                          <React.Fragment key={type}>
                            <span id={name} className={`${iconClass} ico--md`} />
                            <Tooltip
                              target={`#${name}`}
                              className="tooltip--primary tooltip--top"
                              position="top"
                              showDelay={5}
                            >
                              {getMonitorName(type)}
                            </Tooltip>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  );
                }}
              />
              <Column
                field="averagePosition"
                header={t('averagePosition')}
                headerClassName="table__header table__header--center"
                bodyClassName="table__cell--center"
                style={{ width: '110px' }}
              />
              <Column
                field="averageShareOfVoice"
                header={t('shareOfVoice')}
                headerClassName="table__header table__header--center"
                bodyClassName="table__cell--center"
                style={{ width: '110px' }}
              />
              <Column expander={true} style={{ width: '25px' }} />
            </DataTable>
          )}
        </>
      )}
    </>
  );
};

export default Stats;
